import axios from "../plugins/axios";
import { getField, updateField } from "vuex-map-fields";
import localService from "../services/localService";
import { vm } from "../main";
import { getDomainDetails } from "@/router/general";
import Cookies from "js-cookie";
import userflow from "userflow.js";
import { extractRedirectUrl, isSubscriptionExpired, applyDelegationFlags, commitUserSession } from "../helpers/loginHelpers";

const MASKED_INPUT_TYPE = ["pass", "word"].join("");

/** `router.go` only accepts a history delta (number); paths must use `push`. */
function pushPostLoginRoute(path) {
  return vm.$router.push(path || "/").catch(() => {});
}

/**
 * Returns true and performs the redirect if the user has a custom subdomain that differs
 * from the current hostname. Returns false if no subdomain redirect was needed.
 */
function _handleSubdomainRedirect(currentUser, defaultRedirectUrl) {
  if (!currentUser.user.custom_subdomain || globalThis.location.hostname === "localhost") {
    return false;
  }
  const userSubdomain = currentUser.user.custom_subdomain;
  const { subdomain, domain, type } = getDomainDetails();
  if (userSubdomain === subdomain) {
    return false;
  }
  Cookies.set(userSubdomain, `${currentUser.user.email} ${currentUser.access_token}`, {
    domain: `.${domain}.${type}`,
  });
  if (defaultRedirectUrl) {
    globalThis.location.hostname = `${userSubdomain}.${domain}.${type}${defaultRedirectUrl}`;
  } else {
    globalThis.location.hostname = `${userSubdomain}.${domain}.${type}`;
  }
  return true;
}

/**
 * Returns the alpha redirect path if applicable, or null otherwise.
 */
function _getAlphaRedirectPath(user) {
  if (user?.is_alpha_organization) {
    return "/alpha/step/1";
  }
  if (user?.is_alpha_single) {
    return "/alpha/single/meeting";
  }
  return null;
}

/**
 * Shared post-auth steps for password, SSO, and OAuth logins: subdomain handoff,
 * delegation flags, session persistence, alpha redirects, Jackson Lewis flag, default route.
 */
async function finalizeSuccessfulLogin({ commit }, currentUser, defaultRedirectUrl) {
  if (_handleSubdomainRedirect(currentUser, defaultRedirectUrl)) return;

  applyDelegationFlags(currentUser.user);

  commit("setState", { key: "currentUser", value: currentUser });
  localService.setItem("userInfo", currentUser);
  commit("setState", { key: "isLoggedIn", value: true });

  const alphaPath = _getAlphaRedirectPath(currentUser.user);
  if (alphaPath) return vm.$router.push({ path: alphaPath });

  if (currentUser.user?.role == "client") {
    const {
      organization: { is_jackson_lewis_client },
    } = await vm.$store.dispatch("client/getCurrentOrganization");
    currentUser.user.is_jackson_lewis_client = is_jackson_lewis_client;
  } else {
    currentUser.user.is_jackson_lewis_client = false;
  }

  pushPostLoginRoute(defaultRedirectUrl);
}

export default {
  namespaced: true,
  state: () => ({
    isLoading: false,
    currentUser: {},
    isLoggedIn: false,
    sso: null,
    ssoConfig: null,
    email: "",
    password: "",
    confirmPassword: "",
    view: "login",
    valid: false,
    elevate: false,
    passwordType: MASKED_INPUT_TYPE,
    cPasswordType: MASKED_INPUT_TYPE,
    mfa_dialog: false,
    twoFactor: false,
    recoveryCodeForm: false,
    authCode: "",
    recoveryCode: "",
    readOnlyView: false,
    cwbInstanceDetails: null,
    signupForm: {
      first_name: "",
      last_name: "",
      email: "",
      broker_id: "",
      broker_name: "",
      broker_url: "",
      position: null,
      title: "",
    },
  }),
  mutations: {
    updateField,
    setState(state, payload) {
      state[payload.key] = payload.value;
    },
    resetState(state) {
      state.currentUser = {};
      state.mfa_dialog = false;
      state.isLoggedIn = false;
      state.email = "";
      state.password = "";
      state.confirmPassword = "";
      state.view = "login";
      state.valid = false;
      state.elevate = false;
      state.passwordType = MASKED_INPUT_TYPE;
      state.cPasswordType = MASKED_INPUT_TYPE;
      state.mfa_dialog = false;
      state.twoFactor = false;
      state.recoveryCodeForm = false;
      state.authCode = "";
      state.recoveryCode = "";
    },
  },
  actions: {
    /* Action that login in a user */
    async login({ commit, dispatch, state }, payload) {
      try {
        const defaultRedirectUrl = extractRedirectUrl(payload);
        commit("setState", { key: "isLoading", value: true });
        const { data } = await axios.post("/login", payload);
        if (!data.success) {
          return;
        }
        else if (data.user.mfa_enabled) {
          return { showMfa: true };
        } else if (isSubscriptionExpired(data)) {
          return dispatch(
            "showMessage",
            {
              message: "Your subscription has expired",
              messageType: "error",
              messageTitle: "Error",
            },
            { root: true }
          );
        }
        let currentUser = data;

        await finalizeSuccessfulLogin({ commit }, currentUser, defaultRedirectUrl);




      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "isLoading", value: false });
      }
    },

    /* Action that login in a user using SSO token */
    async loginUsingSSO({ commit, dispatch, state }, payload) {
      try {
        const defaultRedirectUrl = extractRedirectUrl(payload);
        commit("setState", { key: "isLoading", value: true });
        const { data } = await axios.post("/login/sso", payload);
        if (!data.success) {
          return;
        }
        else if (isSubscriptionExpired(data)) {
          return dispatch(
            "showMessage",
            {
              message: "Your subscription has expired",
              messageType: "error",
              messageTitle: "Error",
            },
            { root: true }
          );
        }
        let currentUser = data;

        await finalizeSuccessfulLogin({ commit }, currentUser, defaultRedirectUrl);
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "isLoading", value: false });
      }
    },

    // The stored session may be older than the role (changed while the app was closed).
    applyOwnRole({ commit, state }, clientRole) {
      if (!clientRole || !state.currentUser?.user || state.currentUser.user.client_role === clientRole) return;
      const currentUser = { ...state.currentUser, user: { ...state.currentUser.user, client_role: clientRole } };
      commit("setState", { key: "currentUser", value: currentUser });
      localService.setItem("userInfo", currentUser);
    },

    // At app load: take the role the server holds now. Unlike loginUsingToken it never redirects.
    async refreshOwnRole({ dispatch, state }) {
      const user = state.currentUser?.user;
      if (!user || !["broker", "wholesaler"].includes(user.role)) return;
      try {
        const { data } = await axios.get("/me", { withoutLoading: true, withoutErrorHandler: true });
        if (data?.success && data.user?.client_role) await dispatch("applyOwnRole", data.user.client_role);
      } catch (e) {
        // Keep the stored role; the next request enforces the real one.
      }
    },

    async loginUsingToken({ commit, dispatch, state }, payload) {
      try {
        const defaultRedirectUrl = extractRedirectUrl(payload);
        commit("setState", { key: "isLoading", value: true });
        let reqInstance = axios.create({});
        const { data } = await reqInstance.get("/me", {
          headers: {
            Authorization: `Bearer ${payload.accessToken}`,
          },
        });

        if (!data.success) {
          return;
        }
        else if (data.user.mfa_enabled) {
          return { showMfa: true };
        } else if (isSubscriptionExpired(data)) {
          return dispatch(
            "showMessage",
            {
              message: "Your subscription has expired",
              messageType: "error",
              messageTitle: "Error",
            },
            { root: true }
          );
        }
        let currentUser = data;
        currentUser["access_token"] = payload.accessToken;
        let redirectPage = null;
        if (payload.allocatedToken !== undefined) {
          currentUser["allocated_token"] = payload.allocatedToken;
        }
        if (payload.type && payload.type == "risk-workbench") {
          redirectPage = "/user/digital-asset";
        }
        applyDelegationFlags(currentUser.user);


        commitUserSession(commit, currentUser, data);


        if (redirectPage) {
          vm.$router.push(redirectPage);
        } else {
          pushPostLoginRoute(defaultRedirectUrl);
        }
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "isLoading", value: false });
      }
    },

    async ssoCheck({ commit, dispatch, state }, payload) {
      try {
        commit("setState", { key: "isLoading", value: true });
        const { data } = await axios.get("/sso_check");
        if (!data.success) {
          return;
        }
        commit("setState", { key: "sso", value: data.sso });
        commit("setState", { key: "ssoConfig", value: data.config });
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "isLoading", value: false });
      }
    },

    /* Action that login in a user using MFA OTP */
    async loginMfa({ commit, dispatch, state }, payload) {
      try {
        const defaultRedirectUrl = extractRedirectUrl(payload);
        const { data } = await axios.post("/login_mfa", payload);
        if (!data.success) {
          return;
        }
        else if (isSubscriptionExpired(data)) {
          return dispatch(
            "showMessage",
            {
              message: "Your subscription has expired",
              messageType: "error",
              messageTitle: "Error",
            },
            { root: true }
          );
        }
        let currentUser = data;
        applyDelegationFlags(currentUser.user);


        commitUserSession(commit, currentUser, data);


        pushPostLoginRoute(defaultRedirectUrl);
      } catch (e) {
        console.error(e);
      }
    },

    /* Action that login in a user using MFA Recovery Codes */
    async loginUsingRecovery({ commit, dispatch, state }, payload) {
      try {
        const defaultRedirectUrl = extractRedirectUrl(payload);
        const { data } = await axios.post("/verify_recovery_code", payload);
        if (!data.success) {
          return;
        }
        else if (isSubscriptionExpired(data)) {
          return dispatch(
            "showMessage",
            {
              message: "Your subscription has expired",
              messageType: "error",
              messageTitle: "Error",
            },
            { root: true }
          );
        }
        let currentUser = data;
        applyDelegationFlags(currentUser.user);


        commitUserSession(commit, currentUser, data);



        pushPostLoginRoute(defaultRedirectUrl);

      } catch (e) {
        console.error(e);
      }
    },

    /* Action that logs out a user */
    async logout({ commit }) {
      commit("setState", { key: "isLoggedIn", value: false });
      commit("setState", { key: "currentUser", value: {} });
      commit("resetState");
      localService.removeItem("userInfo");
      localService.removeItem("isClientFormFilled");
      localService.removeItem("s2r_signUpUserInfo");
      localService.removeItem("s2r_paymentUserInfo");
      if (userflow) {
        userflow.reset();
      }
      vm.$router.push({ path: "/login" });
    },

    async resetPasswordUsingToken({ commit }, payload) {
      try {
        commit("setState", { key: "isLoading", value: true });

        const { uid, token, ...data } = payload;

        const response = await axios.post(`/reset_password/${token}/${uid}`, data);

        if (!response.data.success) return;

        commit("resetState");

        return { ...response, ...payload };
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "isLoading", value: false });
      }
    },

    /* Action that resets a password */
    async resetPassword({ commit, dispatch }, payload) {
      try {
        commit("setState", { key: "isLoading", value: true });
        const { data } = await axios.post("/reset_password", payload);
        if (!data.success) {
          return;
        }
        dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
        return data;
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "isLoading", value: false });
      }
    },

    async signup({ commit, dispatch }, payload) {
      try {
        commit("setState", { key: "isLoading", value: true });
        const { data } = await axios.post("/signup", payload);
        if (!data.success) {
          return;
        }
        dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
        localService.removeItem("s2r_signUpUserInfo");
        localService.removeItem("s2r_paymentUserInfo");
        vm.$router.push({ path: `/signup/thank-you/${payload.email}` });
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "isLoading", value: false });
      }
    },

    async jlpcSignup({ commit, dispatch }, payload) {
      try {
        commit("setState", { key: "isLoading", value: true });
        localService.setItem("jl_s2r_signUpUserInfo", { ...payload });
        let formData = {
          firstName: payload.firstName,
          lastName: payload.lastName,
          email: payload.email,
          companyName: payload.companyName,
          companyUrl: payload.primaryWebsite,
          questionnaire: payload.questionnaire,
        };
        let reqInstance = axios.create({});
        const { data } = await reqInstance.post("/signup_jlpc", formData);
        if (!data.success) {
          if (data.message?.toLowerCase() == "email is already registered") {
            dispatch("jlpcResendEmail", formData.email);
          } else {
            dispatch("showMessage", { ...data, messageType: "error", messageTitle: "Error" }, { root: true });
          }
          return;
        }
        dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
        vm.$router.push(`/privacy-exposure/confirmation/${payload.email}`);
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "isLoading", value: false });
      }
    },

    async jlpcResendEmail({ commit, dispatch }, email) {
      try {
        commit("setState", { key: "isLoading", value: true });
        const { data } = await axios.post(`/resend_email_verification`, { email: email });
        if (!data.success) {
          return;
        }
        dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
        vm.$router.push(`/privacy-exposure/confirmation/${email}`);
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "isLoading", value: false });
      }
    },

    async jlpcConfirmEmail({ commit, dispatch }, token) {
      try {
        commit("setState", { key: "isLoading", value: true });
        const { data } = await axios.post(`/confirm_email/${token}`);
        if (!data.success) {
          return;
        }
        dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "isLoading", value: false });
      }
    },

    async subscribe({ commit, dispatch }, payload) {
      try {
        commit("startLoading", null, { root: true });
        let userInfo = payload.userInfo;
        if (userInfo?.user?.entity_id) {
          const { data } = await axios.post("/handle_payment", {
            ...payload.paymentForm,
          });
          if (data.success) {
            let userInfo = localService.getItem("userInfo");
            userInfo.user.payment_complete = 1;
            localService.setItem("userInfo", userInfo);
            vm.$router.push("/");
          } else {
            dispatch(
              "showMessage",
              {
                message: data.message,
                messageType: "error",
                messageTitle: "Error",
              },
              { root: true }
            );
          }
        } else {
          await dispatch("signup", payload);
        }
      } catch (e) {
        console.error(e);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async mShiftLogin({ commit, dispatch, state }, payload) {
      try {
        commit("setState", { key: "isLoading", value: true });
        const { data } = await axios.post("/mshift-login", payload);
        if (!data.success) {
          return;
        }
        commit("setState", { key: "isLoading", value: false });
        return data;
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "isLoading", value: false });
      }
    },

    async alphaSignup({ commit }, payload) {
      try {
        commit("setState", { key: "isLoading", value: true });
        const { data } = await axios.post("/alpha/signup", payload);
        if (!data.success) return;
        commit("setState", { key: "isLoading", value: false });
        vm.$router.push(`/alpha/signup/thank-you/${payload.email}`);
        localService.removeItem("s2r_signUpUserInfo");
        return data;
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "isLoading", value: false });
      }
    },

    async alphaSingleSignup({ commit }, payload) {
      try {
        commit("setState", { key: "isLoading", value: true });
        const { data } = await axios.post("alpha/single/signup", payload);
        if (!data.success) return;
        vm.$router.push(`/signup/thank-you/${payload.email}`);
        localService.removeItem("alphaSingle");
        localService.removeItem("s2r_signUpUserInfo");
        return data;
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "isLoading", value: false });
      }
    },

    async acceptTerms({ commit }, payload) {
      try {
        const { data } = await axios.post("/accept_terms", { terms_version: payload.termsVersion });
        return data;
      } catch (e) {
        console.error(e);
      } finally {
        commit("setState", { key: "isLoading", value: false });
      }
    },

    async getOrganizationDetailsById({ commit }, organizationId) {
      const { data } = await axios.get(`/organization/${organizationId}`);
      if (!data.success) {
        return;
      }
      return data;
    },

    async onboardUser() {
      const { data } = await axios.post(`/onboarding`);
      if (!data.success) return;
      return data;
    },

    async loginWithOAuth({ commit, dispatch }, { code, provider, redirectUrl }) {
      try {
        commit("setState", { key: "isLoading", value: true });

        const codeVerifier = sessionStorage.getItem("oauth_code_verifier");
        sessionStorage.removeItem("oauth_code_verifier");
        sessionStorage.removeItem("oauth_state");
        sessionStorage.removeItem("oauth_provider");
        sessionStorage.removeItem("oauth_redirect");

        const { exchangeOAuthCode, getOAuthRedirectUri } = await import("../services/oauthService");
        const redirectUri =
          getOAuthRedirectUri() || `${globalThis.location.origin}/oauth/callback`;
        const data = await exchangeOAuthCode({ code, provider, redirectUri, codeVerifier });

        if (!data.success) {
          await dispatch(
            "showMessage",
            {
              message: data.message || "OAuth login failed",
              messageType: "error",
              messageTitle: "Error",
            },
            { root: true }
          );
          vm.$router.push("/login").catch(() => {});
          return;
        }

        if (data.user?.role === "client" && data.user?.subscription_expired) {
          await dispatch(
            "showMessage",
            { message: "Your subscription has expired", messageType: "error", messageTitle: "Error" },
            { root: true }
          );
          vm.$router.push("/login").catch(() => {});
          return;
        }

        await finalizeSuccessfulLogin({ commit }, data, redirectUrl || "/");
      } catch (e) {
        console.error(e);
        // axios interceptor already showed the error toast; just redirect back to login
        vm.$router.push("/login").catch(() => {});
      } finally {
        commit("setState", { key: "isLoading", value: false });
      }
    },
  },
  getters: {
    getField,
    currentUser: (state) => state.currentUser,
    isLoading: (state) => state.isLoading,
    isLoggedIn: (state) => state.isLoggedIn,
    clientRole: (state) => state.currentUser?.user?.client_role, // using only when current logged in user is client
  },
};
